import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, ReplaySubject, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Base, COUNTRIES, ListQueryParam } from '../../../common';
import { ButtonAction, ButtonType } from '../../../ui-kit/action-button';
import { FilterSearch, FilterSettings } from '../../../ui-kit/list-filter';
import { CountryListSearchCriteria } from '../models/country-selection.model';

@Component({
    selector: 'lib-country-selection-dialog',
    templateUrl: './country-dialog.component.html',
    styleUrls: ['./country-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CountrySelectionDialogComponent implements OnInit {
  displayedColumns = ['select', 'name', 'id'];
  filterConfig: FilterSettings;
  buttonAction = ButtonAction;
  buttonType = ButtonType;
  selectedRows = new Map<string, Base>();
  hasValue$ = new BehaviorSubject<boolean>(false);
  optionItems$ = new BehaviorSubject<Base[]>([]);
  filteredCountries: Base[] = [];
  filteredOptionItems$: ReplaySubject<Base[]> = new ReplaySubject<Base[]>(1);
  subscriptions: Subscription[] = [];
  countryIds: string[] = [];
  private _optionItems: Base[] = [];

  constructor(
    public dialogRef: MatDialogRef<CountrySelectionDialogComponent>,

    @Inject(MAT_DIALOG_DATA) public data: Base[],
  ) {}

  ngOnInit(): void {
    this.countryIds = this.data.map((country: Base) => country.id);
    this.optionItems$.next((this._optionItems = COUNTRIES));
    this.updateDataSource();
    this.subscriptions.push(
      this.optionItems$.pipe(filter(Boolean)).subscribe((optionItems: Base[]) => {
        this.filteredOptionItems$.next(optionItems.slice());
      }),
    );
    this.initFiltersForm();
  }

  changed(event: CountryListSearchCriteria): void {
    this.filterOptionItems(event.filter);
  }

  noCountryExist(): boolean {
    return this._optionItems && this._optionItems.length === 0;
  }

  confirm(): void {
    this.dialogRef.close({ selected: [...this.selectedRows.values()] });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  isAllSelected(): boolean {
    return this.filteredCountries.every((row: Base) => this.selectedRows.has(row.id));
  }

  masterToggle(shouldAdd: boolean): void {
    this.filteredOptionItems$.pipe(take(1)).subscribe((optionItems: Base[]) => {
      optionItems.forEach((row: Base) => this.updateSelectedHubs(row, shouldAdd));
    });
    this.hasValue$.next(this.hasValue());
  }

  toggle(row: Base, shouldAdd: boolean): void {
    this.updateSelectedHubs(row, shouldAdd);
    this.hasValue$.next(this.hasValue());
  }

  private initFiltersForm(): void {
    this.filterConfig = {
      items: [new FilterSearch({ name: ListQueryParam.Filter })],
    };
  }

  private updateSelectedHubs(row: Base, shouldAdd: boolean = true): void {
    shouldAdd ? this.selectedRows.set(row.id, row) : this.selectedRows.delete(row.id);
  }

  private hasValue(): boolean {
    if (!this.filteredCountries) {
      return this._optionItems.some((row: Base) => this.selectedRows.has(row.id));
    } else {
      return this.filteredCountries.some((row: Base) => this.selectedRows.has(row.id));
    }
  }

  private filterOptionItems(filterString: string): void {
    if (!this._optionItems) {
      return;
    }
    let search = filterString;
    if (!search) {
      this.filteredOptionItems$.next(this._optionItems.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredCountries = this._optionItems.filter(
      (optionItem: Base) => optionItem.name.toLowerCase().indexOf(search) > -1,
    );
    this.filteredOptionItems$.next(
      this._optionItems.filter((optionItem: Base) => optionItem.name.toLowerCase().indexOf(search) > -1),
    );
    this.hasValue$.next(this.hasValue());
  }

  private updateDataSource(): void {
    this._optionItems
      .filter((row: Base) => this.countryIds.includes(row.id))
      .forEach((row: Base) => this.updateSelectedHubs(row));
    this.filteredCountries = this._optionItems;
    this.hasValue$.next(this.hasValue());
  }
}
